import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import * as z from "zod";
import { useEffect } from "react";
import { useOpenSession } from "@src/api/useOpenSession";
import userAgreementPdf from "@src/assets/pdf/WBG_User_Agreement_21122023.pdf";
import personalDataProcessingPdf from "@src/assets/pdf/WB_Consent_on_Personal_Data_21122023.pdf";
import privacyPolicyPdf from "@src/assets/pdf/WB_Privacy_Policy_21122023.pdf";
import userAgreementPdfKA from "@src/assets/pdf/ka/WBG_User_Agreement_22122023.pdf";
import personalDataProcessingPdfKA from "@src/assets/pdf/ka/WB_Consent_on_Personal_Data_22122023.pdf";
import privacyPolicyPdfKA from "@src/assets/pdf/ka/WB_Privacy_Policy_22122023.pdf";
import Container from "@src/components/shared/Container";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import { Checkbox } from "@src/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@src/components/ui/form";
import useZustandStore from "@src/helpers/zustand";
import { LoggerTypes, useKYCAddUserLog } from "@src/api/useKYCAddUserLog";
import { useNavigate } from "react-router-dom";

const FormSchema = z.object({
  privacyPolicy: z.boolean().refine((val) => val, {
    message: "სავალდებულო ველი",
  }),
  personalDataProcessing: z.boolean().refine((val) => val, {
    message: "სავალდებულო ველი",
  }),
  userAgreement: z.boolean().refine((val) => val, {
    message: "სავალდებულო ველი",
  }),
});

function Terms() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const openSessionMutation = useOpenSession();
  const { data: globalState } = useZustandStore((state) => state);
  const addUserLogMutation = useKYCAddUserLog();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      privacyPolicy: false,
      personalDataProcessing: false,
      userAgreement: false,
    },
  });

  useEffect(() => {
    addUserLogMutation.mutate({
      eventName: LoggerTypes.Terms,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit() {
    if (globalState.isManual) {
      navigate(`/${i18n.language}/contact`);
    } else {
      openSessionMutation.mutate(globalState.apiKey);
    }
  }

  return (
    <Container>
      <Form {...form}>
        <div className="py-4 max-sm:mt-14">
          <TitleAndDesc desc={t("terms.desc")} title={t("terms.title")} />
        </div>
        <p>
          <Trans
            components={{
              1: <li className="mb-3 text-sm text-gray-600" />,
            }}
            i18nKey="terms.description"
          />
        </p>
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className=" flex flex-col gap-4 rounded-lg border border-solid border-gray-200 bg-white p-4">
            <FormField
              control={form.control}
              name="privacyPolicy"
              render={({ field }) => {
                return (
                  <FormItem className="rounded-6 flex items-center gap-2 bg-white px-4 md:px-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 text-sm font-normal leading-6 cursor-pointer">
                      <Trans
                        className="text-sm"
                        components={{
                          1: (
                            <a
                              className="text-whitebitBlack inline-block cursor-pointer font-medium underline"
                              href={
                                i18n.language === "ka"
                                  ? privacyPolicyPdfKA
                                  : privacyPolicyPdf
                              }
                              rel="noreferrer"
                              target="_blank"
                            >
                              {" "}
                            </a>
                          ),
                        }}
                        i18nKey="terms.agreeTexts.privacyPolicy"
                      />
                    </FormLabel>
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="personalDataProcessing"
              render={({ field }) => {
                return (
                  <FormItem className="rounded-6 flex items-center gap-2 bg-white px-4 md:px-0">
                    <FormControl>
                      <Checkbox
                        // className={cn(form.formState.errors.copyAgree && 'border-red-500')}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 cursor-pointer">
                      <Trans
                        components={{
                          1: (
                            <a
                              className="text-whitebitBlack inline-block cursor-pointer font-medium underline"
                              href={
                                i18n.language === "ka"
                                  ? personalDataProcessingPdfKA
                                  : personalDataProcessingPdf
                              }
                              rel="noreferrer"
                              target="_blank"
                            >
                              {" "}
                            </a>
                          ),
                        }}
                        i18nKey="terms.agreeTexts.personalDataProcessing"
                      />
                    </FormLabel>
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="userAgreement"
              render={({ field }) => {
                return (
                  <FormItem className="rounded-6 flex items-center gap-2 bg-white px-4 md:px-0">
                    <FormControl>
                      <Checkbox
                        // className={cn(form.formState.errors.copyAgree && 'border-red-500')}
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 cursor-pointer">
                      <Trans
                        components={{
                          1: (
                            <a
                              className="text-whitebitBlack inline-block cursor-pointer font-medium underline"
                              href={
                                i18n.language === "ka"
                                  ? userAgreementPdfKA
                                  : userAgreementPdf
                              }
                              rel="noreferrer"
                              target="_blank"
                            >
                              {" "}
                            </a>
                          ),
                        }}
                        i18nKey="terms.agreeTexts.userAgreement"
                      />
                    </FormLabel>
                  </FormItem>
                );
              }}
            />
          </div>
          <div className="">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button className="w-full" disabled={!form.formState.isValid}>
                {t("startIdentification.next")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}

export default Terms;
